import { Component, inject, input, output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { InputTextModule } from 'primeng/inputtext';
import { LocalizationService } from '../../../core/localization/localization.service';
import { AinFormFieldComponent } from '../../../shared/design-system/form-field.component';
import { InfoHintComponent } from '../../../shared/design-system/info-hint.component';
import { FormField, LocalizedText } from '../../../shared/dynamic-form/form-schema.models';
import { FormBuilderLocalizedInputComponent } from './form-builder-localized-input.component';

@Component({
  standalone: true,
  selector: 'ain-form-builder-matrix-panel',
  imports: [
    FormsModule,
    ButtonModule,
    CheckboxModule,
    InputTextModule,
    AinFormFieldComponent,
    InfoHintComponent,
    FormBuilderLocalizedInputComponent
  ],
  template: `
    <section class="logic-panel">
      <ain-info-hint
        [titleKey]="'dynamic.builder.matrix.infoTitle'"
        [descriptionKey]="'dynamic.builder.matrix.infoDescription'"
        [bulletKeys]="['dynamic.builder.matrix.infoBullet']" />

      <div class="checkbox-row">
        <p-checkbox
          binary
          [ngModel]="!!field().matrix?.multiple"
          [disabled]="!canEdit()"
          (ngModelChange)="updateMultiple($event)" />
        <label>{{ t('dynamic.builder.matrix.multiple') }}</label>
      </div>

      <div class="header-row">
        <strong>{{ t('dynamic.builder.matrix.rows') }}</strong>
        @if (canEdit()) {
          <button pButton type="button" text icon="pi pi-plus" [label]="t('dynamic.builder.matrix.addRow')" (click)="addRow()"></button>
        }
      </div>
      @for (row of field().matrix?.rows ?? []; track row.key; let idx = $index) {
        <article class="axis-card">
          <ain-form-builder-localized-input
            [label]="t('dynamic.builder.matrix.rowLabel')"
            [text]="row.label"
            [disabled]="!canEdit()"
            (textChange)="updateRowLabel(idx, $event)" />
          <ain-form-field [label]="t('dynamic.builder.matrix.rowKey')">
            <input pInputText [ngModel]="row.key" [disabled]="!canEdit()" (ngModelChange)="updateRowKey(idx, $event)" />
          </ain-form-field>
          @if (canEdit()) {
            <button pButton type="button" severity="danger" text icon="pi pi-trash" (click)="removeRow(idx)"></button>
          }
        </article>
      }

      <div class="header-row">
        <strong>{{ t('dynamic.builder.matrix.columns') }}</strong>
        @if (canEdit()) {
          <button pButton type="button" text icon="pi pi-plus" [label]="t('dynamic.builder.matrix.addColumn')" (click)="addColumn()"></button>
        }
      </div>
      @for (column of field().matrix?.columns ?? []; track column.key; let idx = $index) {
        <article class="axis-card">
          <ain-form-builder-localized-input
            [label]="t('dynamic.builder.matrix.columnLabel')"
            [text]="column.label"
            [disabled]="!canEdit()"
            (textChange)="updateColumnLabel(idx, $event)" />
          <ain-form-field [label]="t('dynamic.builder.matrix.columnKey')">
            <input pInputText [ngModel]="column.key" [disabled]="!canEdit()" (ngModelChange)="updateColumnKey(idx, $event)" />
          </ain-form-field>
          @if (canEdit()) {
            <button pButton type="button" severity="danger" text icon="pi pi-trash" (click)="removeColumn(idx)"></button>
          }
        </article>
      }
    </section>
  `,
  styles: [`
    .logic-panel { display: grid; gap: 12px; }
    .header-row { display: flex; justify-content: space-between; align-items: center; gap: 8px; }
    .axis-card { border: 1px solid #e2e8f0; border-radius: 8px; padding: 10px; display: grid; gap: 8px; }
    .checkbox-row { display: flex; align-items: center; gap: 8px; }
  `]
})
export class FormBuilderMatrixPanelComponent {
  private readonly localization = inject(LocalizationService);

  readonly field = input.required<FormField>();
  readonly canEdit = input(false);
  readonly fieldChange = output<FormField>();

  t(key: string): string {
    return this.localization.translate(key);
  }

  private matrix() {
    return this.field().matrix ?? { rows: [], columns: [], multiple: false };
  }

  private emit(matrix: NonNullable<FormField['matrix']>): void {
    this.fieldChange.emit({ ...this.field(), matrix });
  }

  updateMultiple(multiple: boolean): void {
    this.emit({ ...this.matrix(), multiple });
  }

  addRow(): void {
    const rows = [...(this.matrix().rows ?? [])];
    rows.push({ key: `row_${rows.length + 1}`, label: { default: `Row ${rows.length + 1}` } });
    this.emit({ ...this.matrix(), rows });
  }

  removeRow(index: number): void {
    const rows = [...(this.matrix().rows ?? [])];
    rows.splice(index, 1);
    this.emit({ ...this.matrix(), rows });
  }

  updateRowLabel(index: number, label: LocalizedText): void {
    const rows = [...(this.matrix().rows ?? [])];
    rows[index] = { ...rows[index], label };
    this.emit({ ...this.matrix(), rows });
  }

  updateRowKey(index: number, key: string): void {
    const rows = [...(this.matrix().rows ?? [])];
    rows[index] = { ...rows[index], key: key.trim() || rows[index].key };
    this.emit({ ...this.matrix(), rows });
  }

  addColumn(): void {
    const columns = [...(this.matrix().columns ?? [])];
    columns.push({ key: `col_${columns.length + 1}`, label: { default: `Column ${columns.length + 1}` } });
    this.emit({ ...this.matrix(), columns });
  }

  removeColumn(index: number): void {
    const columns = [...(this.matrix().columns ?? [])];
    columns.splice(index, 1);
    this.emit({ ...this.matrix(), columns });
  }

  updateColumnLabel(index: number, label: LocalizedText): void {
    const columns = [...(this.matrix().columns ?? [])];
    columns[index] = { ...columns[index], label };
    this.emit({ ...this.matrix(), columns });
  }

  updateColumnKey(index: number, key: string): void {
    const columns = [...(this.matrix().columns ?? [])];
    columns[index] = { ...columns[index], key: key.trim() || columns[index].key };
    this.emit({ ...this.matrix(), columns });
  }
}
